import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Shield, Award, Zap, Target } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import QuoteForm from '../QuoteForm';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';

const HeroSection = ({ quoteDialogOpen, setQuoteDialogOpen, content }) => {
  const site = useSiteSettings();
  const business = content ? { name: content.name } : site.business;
  const homepage = content ? {
    headline: content.headline,
    subline: content.subline,
    primary_cta_text: content.primary_cta_text,
    primary_cta_link: content.primary_cta_link,
    secondary_cta_text: content.secondary_cta_text,
    secondary_cta_link: content.secondary_cta_link,
  } : site.homepage;
  const logoUrl = content?.logo_url || '/logo-taylordarms.png';
  const tagline = content?.tagline || 'Faith. Family. Firearms.';
  const primaryOpensDialog = !homepage?.primary_cta_link || homepage.primary_cta_link.includes('quote');
  const secondaryLink = homepage?.secondary_cta_link || '/#services';
  const secondaryIsRoute = secondaryLink.startsWith('/') && !secondaryLink.startsWith('/#');
  const secondaryIsAnchor = secondaryLink.startsWith('/#') || secondaryLink.startsWith('#');

  const handleSecondary = (e) => {
    if (secondaryIsRoute) return;
    if (secondaryIsAnchor) {
      e.preventDefault();
      const anchor = secondaryLink.replace(/^\/#/, '#');
      const el = document.querySelector(anchor);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="relative pt-20 min-h-screen flex items-center overflow-hidden">
      {/* Background with overlay - flag is in App.js globally */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 opacity-10" 
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgb(192 192 192) 1px, transparent 0)',
            backgroundSize: '40px 40px'
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="text-white">{business?.name?.replace(' LLC', '') || 'TayLord Arms'}</span>
              <br />
              <span className="text-white text-4xl md:text-5xl lg:text-6xl pl-8 block mt-2" data-testid="hero-headline">{homepage?.headline || 'Gunsmithing Services'}</span>
            </h1>

            <p className="text-white text-lg md:text-xl leading-relaxed max-w-xl" data-testid="hero-subline">
              {homepage?.subline || 'From custom builds to precision repairs, we deliver craftsmanship and reliability you can trust. Your firearms deserve the best.'}
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              {primaryOpensDialog ? (
                <Dialog open={quoteDialogOpen} onOpenChange={setQuoteDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      size="lg"
                      data-testid="hero-primary-cta"
                      className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-semibold group"
                    >
                      {homepage?.primary_cta_text || 'Get Free Quote'}
                      <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto z-[100]">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-bold text-white">Request a Quote</DialogTitle>
                    </DialogHeader>
                    <QuoteForm onClose={() => setQuoteDialogOpen(false)} />
                  </DialogContent>
                </Dialog>
              ) : (
                <Button asChild size="lg" data-testid="hero-primary-cta" className="bg-red-600 hover:bg-red-700 text-white px-8 py-6 text-lg font-semibold group">
                  <a href={homepage.primary_cta_link}>{homepage?.primary_cta_text || 'Get Free Quote'} <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" /></a>
                </Button>
              )}

              {secondaryIsRoute ? (
                <Button asChild size="lg" variant="outline" data-testid="hero-secondary-cta"
                  className="border-slate-700 text-white hover:bg-slate-800 px-8 py-6 text-lg font-semibold">
                  <Link to={secondaryLink}>{homepage?.secondary_cta_text || 'View Services'}</Link>
                </Button>
              ) : (
                <Button
                  size="lg"
                  variant="outline"
                  data-testid="hero-secondary-cta"
                  className="border-slate-700 text-white hover:bg-slate-800 px-8 py-6 text-lg font-semibold"
                  onClick={handleSecondary}
                >
                  {homepage?.secondary_cta_text || 'View Services'}
                </Button>
              )}
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-6 pt-8 border-t border-slate-700">
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-400/10 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-gray-300" />
                </div>
                <div>
                  <p className="text-white font-semibold">Sig Sauer Certified</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-400/10 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-gray-300" />
                </div>
                <div>
                  <p className="text-white font-semibold">Certified Glock Armorer</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-400/10 rounded-lg flex items-center justify-center">
                  <Shield className="w-6 h-6 text-gray-300" />
                </div>
                <div>
                  <p className="text-white font-semibold">Federal Firearms License (FFL) Holder</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gray-400/10 rounded-lg flex items-center justify-center">
                  <Award className="w-6 h-6 text-gray-300" />
                </div>
                <div>
                  <p className="text-white font-semibold">Veteran Owned</p>
                  <p className="text-white text-sm">Former Law Enforcement Operated</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Logo & Feature Cards */}
          <div className="flex flex-col items-center space-y-4">
            {/* Logo - smaller on mobile, enlarged by 20% */}
            <div className="w-full flex flex-col items-center mb-2">
              <img 
                src={logoUrl}
                alt="TayLord Arms Logo"
                className="w-56 h-56 md:w-64 md:h-64 lg:w-80 lg:h-80 object-contain opacity-90"
              />
              <p className="text-white text-lg md:text-xl font-semibold tracking-wide -mt-4 md:-mt-6 text-center">
                {tagline}
              </p>
            </div>
            
            {/* Feature Cards - responsive grid */}
            <div className="grid grid-cols-2 gap-2 md:gap-3 w-full max-w-sm md:max-w-md">
              <Card className="bg-slate-800/80 border-slate-700 hover:border-gray-400/50 transition-all duration-300 hover:scale-105">
                <CardHeader className="p-3 md:p-4">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-gray-400/10 rounded-lg flex items-center justify-center mb-2">
                    <Zap className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-300" />
                  </div>
                  <CardTitle className="text-white text-xs md:text-sm">Fast Turnaround</CardTitle>
                  <CardDescription className="text-white text-[10px] md:text-xs">Most repairs completed within days</CardDescription>
                </CardHeader>
              </Card>

              <Card className="bg-slate-800/80 border-slate-700 hover:border-gray-400/50 transition-all duration-300 hover:scale-105 mt-3 md:mt-4">
                <CardHeader className="p-3 md:p-4">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-gray-400/10 rounded-lg flex items-center justify-center mb-2">
                    <Award className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-300" />
                  </div>
                  <CardTitle className="text-white text-xs md:text-sm">Expert Craftsmanship</CardTitle>
                  <CardDescription className="text-white text-[10px] md:text-xs">Certified gunsmiths</CardDescription>
                </CardHeader>
              </Card>

              <Card className="bg-slate-800/80 border-slate-700 hover:border-gray-400/50 transition-all duration-300 hover:scale-105">
                <CardHeader className="p-3 md:p-4">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-gray-400/10 rounded-lg flex items-center justify-center mb-2">
                    <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-300" />
                  </div>
                  <CardTitle className="text-white text-xs md:text-sm">Quality Work</CardTitle>
                  <CardDescription className="text-white text-[10px] md:text-xs">Guaranteed satisfaction</CardDescription>
                </CardHeader>
              </Card>

              <Card className="bg-slate-800/80 border-slate-700 hover:border-gray-400/50 transition-all duration-300 hover:scale-105 mt-3 md:mt-4">
                <CardHeader className="p-3 md:p-4">
                  <div className="w-7 h-7 md:w-8 md:h-8 bg-gray-400/10 rounded-lg flex items-center justify-center mb-2">
                    <Target className="w-3.5 h-3.5 md:w-4 md:h-4 text-gray-300" />
                  </div>
                  <CardTitle className="text-white text-xs md:text-sm">Precision</CardTitle>
                  <CardDescription className="text-white text-[10px] md:text-xs">Attention to detail</CardDescription>
                </CardHeader>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
